import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Collections } from '@air/api';
import { type Board, type Clip } from '@air/api/types';
import { callAirApi } from '@air/utils-api';

import { getCollectionKey } from '~/components/ShowcaseModal/hooks/queries/useGetCollection';
import { GALLERY_ASSETS, GALLERY_BOARDS, GALLERY_FILES } from '~/constants/react-query-keys';
import { insertItemsIntoCollectionGalleryQueries } from '~/swr-hooks/gallery/galleryView/insertItemsIntoCollectionGalleryQueries';
import { invalidateCollectionGalleryItemQueries } from '~/swr-hooks/gallery/galleryView/invalidateCollectionGalleryItemQueries';
import { isShortUrlsListKey } from '~/swr-hooks/shortUrl/useListShortUrls';

export type UseAddCollectionItemsMutationParams = Required<NonNullable<Parameters<typeof Collections.Items.add>[0]>> & {
  /**
   * The FULL entities behind the id arrays in the API params. When present, the just-added items
   * are written straight into the mounted collection gallery caches instead of refetching the
   * public listings, whose reads lag the add and can miss the new items (FOUND-477) — see
   * insertItemsIntoCollectionGalleryQueries.
   *
   * Genuinely optional — this mutation has two callers with different knowledge:
   * - The collection link editor's Add-items button (ShowcaseEditorAddItemsButton) confirms
   *   an ItemPickerSelection, which carries full Board/Clip entities — it passes them, and the
   *   editor canvas on screen updates from the cache insert.
   * - The "Add to share link" dropdown on board pages reaches here through
   *   useAddToShowcaseOption → useGetShareLinkMenuOptions, a chain whose signatures carry ONLY
   *   `{ assetIds, boardIds }` (the card/selection menus at the top derived those ids from
   *   entities, but the entities don't survive the chain). It omits `addedItems`, and that's
   *   fine: no collection gallery is mounted on a board page, so there is nothing to insert
   *   into — the invalidation fallback below covers the rare mounted-gallery case and matches
   *   the historical behavior.
   * Threading entities through that dropdown chain (making this required and deleting the
   * fallback) is a known cleanup, deferred because it touches ~10 call sites across both menu
   * generations.
   */
  addedItems?: { boards: Board[]; clips: Clip[] };
  /**
   * Which sub-page's public/canvas gallery the optimistic insert should scope to (see
   * insertItemsIntoCollectionGalleryQueries). `undefined` = the root/Home key, which is what every
   * non-editor caller means — so omitting it preserves today's single-gallery insert exactly. The
   * editor's Add-items button passes the active page so the insert can't bleed across sub-pages.
   */
  subPageCollectionId?: string;
};

export const useAddCollectionItemsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<void, unknown, UseAddCollectionItemsMutationParams>({
    // `addedItems` and `subPageCollectionId` are cache-update context for onSuccess only —
    // destructured off so neither reaches the API payload.
    mutationFn: async ({ addedItems: _addedItems, subPageCollectionId: _subPageCollectionId, ...params }) => {
      await callAirApi(() => Collections.Items.add(params));
    },
    onSuccess: (_data, params) => {
      queryClient.invalidateQueries({ queryKey: getCollectionKey({ collectionId: params.collectionId }) });

      // The added items must appear in whichever collection gallery is on screen (private
      // collection page, editor canvas) — without this, the add gives no visible feedback and
      // users retry against a backend that appends non-idempotently (FOUND-477).
      if (params.addedItems) {
        // The caller handed us the full entities, so write them into the caches directly. Do NOT
        // run invalidateCollectionGalleryItemQueries here: it would refetch the public/canvas
        // shortId listings, whose reads lag the add — a stale response (still without the new
        // items) would clobber this insert and re-freeze the canvas until a manual refresh.
        insertItemsIntoCollectionGalleryQueries({
          queryClient,
          collectionId: params.collectionId,
          // Scope the optimistic insert to the page the caller targeted (`undefined` = root/Home)
          // so it doesn't bleed into a sibling sub-page's canvas cache. The private invalidation
          // below is unaffected — it matches the uuid key, not the public one.
          subPageCollectionId: params.subPageCollectionId,
          boards: params.addedItems.boards,
          clips: params.addedItems.clips,
        });

        // The private collection page's galleries key on the uuid and read consistent private
        // endpoints — invalidate ONLY those so their sort order and pages fully re-sync. The
        // public/canvas `{ collectionId: true }` key shape never matches these exact keys, so the
        // insert above stays untouched.
        for (const galleryKey of [GALLERY_BOARDS, GALLERY_ASSETS, GALLERY_FILES]) {
          queryClient.invalidateQueries({ queryKey: [galleryKey, { collectionId: params.collectionId }] });
        }
      } else {
        // The ids-only dropdown chain (see the addedItems doc above) can't feed the cache insert;
        // the (intermittently stale) public refetch is the best available fallback and matches
        // the historical behavior.
        invalidateCollectionGalleryItemQueries({ queryClient, collectionId: params.collectionId });
      }

      if (params.workspaceId) {
        // Predicate, not a key literal: getShortUrlsListKey({ workspaceId }) alone builds a key whose
        // search/limit/orderBy are undefined, which partial-matching compares against the mounted
        // list's REAL values and never matches — the invalidation was a silent no-op.
        queryClient.invalidateQueries({
          predicate: ({ queryKey }) => isShortUrlsListKey(queryKey) && queryKey[1].workspaceId === params.workspaceId,
        });
      }
    },
  });
};
